import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { useQueryClient } from '@tanstack/react-query';
import {
  CANDIDATE_REDIRECT_KEY,
  CANDIDATE_ROLE_KEY,
  ROUTES,
} from '../../constants';
import { useAuth } from '../../hooks/useAuth';

const SalesLoginPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const isAuthenticated =
    (auth as { isAuthenticated?: boolean }).isAuthenticated || false;
  const isLoadingAuth = (auth as { isLoading?: boolean }).isLoading || false;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isLoadingAuth && isAuthenticated) {
      navigate(ROUTES.HOME, { replace: true });
    }
  }, [isAuthenticated, isLoadingAuth, navigate]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!email.trim() || !password.trim() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await Auth.signIn(email.trim().toLowerCase(), password.trim());
      localStorage.removeItem(CANDIDATE_ROLE_KEY);
      localStorage.removeItem(CANDIDATE_REDIRECT_KEY);
      await queryClient.invalidateQueries({
        queryKey: ['auth', 'currentUser'],
      });
      navigate(ROUTES.HOME, { replace: true });
    } catch (err: unknown) {
      const authError = err as { code?: string; message?: string };
      if (authError.code === 'NotAuthorizedException') {
        setError('Incorrect email or password.');
      } else if (authError.code === 'UserNotConfirmedException') {
        setError('Please verify your email address before signing in.');
      } else {
        setError(authError.message || 'Unable to sign you in right now.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#F6F8FC] px-4 py-8 text-[#0F172A] sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-64px)] max-w-5xl items-center justify-center">
        <div className="grid w-full gap-8 lg:grid-cols-[minmax(0,1.1fr),440px]">
          <section className="hidden rounded-[32px] border border-slate-200 bg-[linear-gradient(160deg,#0F2741_0%,#133C63_45%,#1A5A8E_100%)] p-9 text-white shadow-[0_28px_80px_rgba(15,23,42,0.22)] lg:block">
            <p className="inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em]">
              WiseDoc PLG
            </p>
            <h1 className="mt-6 text-[40px] font-semibold leading-[1.02] tracking-[-0.05em]">
              Sales Dashboard
            </h1>
            <p className="mt-4 max-w-md text-sm leading-7 text-white/74">
              Sign in with your existing WiseDoc account. Access is granted only
              to accepted members of the internal PLG org with the approved
              role.
            </p>

            <div className="mt-10 grid gap-4 text-sm text-white/84">
              <div className="rounded-[22px] border border-white/10 bg-white/6 p-4">
                Organization onboarding visibility
              </div>
              <div className="rounded-[22px] border border-white/10 bg-white/6 p-4">
                Job-level application snapshot
              </div>
              <div className="rounded-[22px] border border-white/10 bg-white/6 p-4">
                Direct lookup by Job ID
              </div>
            </div>
          </section>

          <section className="rounded-[28px] border border-slate-200 bg-white p-7 shadow-[0_20px_60px_rgba(15,23,42,0.08)] sm:p-8">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-700">
                Sign in
              </p>
              <h2 className="mt-3 text-[30px] font-semibold tracking-[-0.04em] text-slate-900">
                Access PLG dashboard
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                Use your current WiseDoc login. Your PLG access is verified
                after sign-in.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <label htmlFor="sales-login-email" className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">
                  Email
                </span>
                <input
                  id="sales-login-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@wisedoc.io"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:bg-white"
                />
              </label>

              <label htmlFor="sales-login-password" className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">
                  Password
                </span>
                <input
                  id="sales-login-password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Enter your password"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:bg-white"
                />
              </label>

              <div className="flex items-center justify-between gap-3">
                <Link
                  to={ROUTES.FORGOT_PASSWORD}
                  className="text-sm font-medium text-blue-700 transition hover:text-blue-800"
                >
                  Forgot password?
                </Link>
              </div>

              {error ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {error}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={!email.trim() || !password.trim() || isSubmitting}
                className="inline-flex w-full items-center justify-center rounded-2xl bg-[linear-gradient(135deg,#0F4C81_0%,#13639F_100%)] px-5 py-3 text-sm font-semibold text-white shadow-[0_14px_28px_rgba(15,76,129,0.22)] transition hover:bg-[linear-gradient(135deg,#0C3D68_0%,#115587_100%)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </button>
            </form>
          </section>
        </div>
      </div>
    </main>
  );
};

export default SalesLoginPage;
